import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import PermissionCheck from '../../../helpers/PermissionCheck'

const ACTIVITY_TYPES = [
  { value: 'treatment_of_disease_disorder_injury', label: 'Treatment of disease, disorder or injury (TDDI)' },
  { value: 'transport_services_triage_medical_advice', label: 'Transport services, triage and medical advice' },
]

// CQC's actual list of service user bands - reproduced here as a practical
// starting point. Not verified against the current CQC application pack, so
// treat this as a drafting aid rather than an exhaustive/authoritative list.
const SERVICE_USER_BAND_OPTIONS = [
  'Children 0-3 years',
  'Children 4-12 years',
  'Young people 13-18 years',
  'Adults 18-65 years',
  'Older people 65+ years',
  'Whole population',
]

// `embedded` renders the builder without its page title (for use inside the
// CQC Compliance Questions page); `onChanged` fires after a successful save
// so the host page can refresh derived state (e.g. question answered-ness).
const SopBuilder = ({ embedded = false, onChanged }) => {
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [sop, setSop] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    API.get('/registration/statement-of-purpose')
      .then((res) => {
        if (res.status === 200) setSop(res.data)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const update = (field) => (value) => setSop((prev) => ({ ...prev, [field]: value }))
  const updateNested = (field, subField) => (value) =>
    setSop((prev) => ({ ...prev, [field]: { ...prev[field], [subField]: value } }))

  const save = () => {
    setSaving(true)
    API.put('/registration/statement-of-purpose', sop)
      .then((res) => {
        if (res.status === 200) {
          setSop(res.data)
          setToast?.({ message: 'Statement of Purpose saved' })
          onChanged?.()
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  if (loading) return <LinearProgress />
  if (!sop) return <Alert severity="error">Unable to load Statement of Purpose.</Alert>

  const addRegulatedActivity = () =>
    update('regulatedActivities')([
      ...(sop.regulatedActivities || []),
      { activityType: ACTIVITY_TYPES[0].value, serviceTypes: [], narrative: '' },
    ])
  const removeRegulatedActivity = (i) =>
    update('regulatedActivities')(sop.regulatedActivities.filter((_, idx) => idx !== i))
  const updateRegulatedActivity = (i, field, value) =>
    update('regulatedActivities')(
      sop.regulatedActivities.map((ra, idx) => (idx === i ? { ...ra, [field]: value } : ra)),
    )

  const addLocation = () =>
    update('locations')([...(sop.locations || []), { name: '', address: '', activitiesCarriedOutHere: [] }])
  const removeLocation = (i) => update('locations')(sop.locations.filter((_, idx) => idx !== i))
  const updateLocation = (i, field, value) =>
    update('locations')(sop.locations.map((loc, idx) => (idx === i ? { ...loc, [field]: value } : loc)))

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          {!embedded && <Typography variant="title1">Statement of Purpose</Typography>}
          <Stack direction="row" gap={1} sx={embedded ? { ml: 'auto' } : undefined}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => navigate('/cqc-centre/registration/statement-of-purpose/pack')}
            >
              Export PDF
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        {!embedded && (
          <Alert severity="warning" variant="outlined">
            This is a drafting aid to help assemble the information CQC's registration application
            typically asks for. It is a pragmatic starting point, not a legally exhaustive replica of
            CQC's current application form - review the finished draft against CQC's own guidance
            before submitting.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1.5 }}>
            Provider details
          </Typography>
          <Stack gap={2}>
            <Stack direction="row" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                label="Legal entity name"
                value={sop.providerDetails?.legalEntityName || ''}
                onChange={(e) => updateNested('providerDetails', 'legalEntityName')(e.target.value)}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <TextField
                size="small"
                label="Legal entity type"
                value={sop.providerDetails?.legalEntityType || ''}
                onChange={(e) => updateNested('providerDetails', 'legalEntityType')(e.target.value)}
                sx={{ flex: 1, minWidth: 220 }}
              />
            </Stack>
            <Stack direction="row" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                label="Registered address"
                value={sop.providerDetails?.registeredAddress || ''}
                onChange={(e) => updateNested('providerDetails', 'registeredAddress')(e.target.value)}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <TextField
                size="small"
                label="CQC Provider ID (if known)"
                value={sop.providerDetails?.cqcProviderId || ''}
                onChange={(e) => updateNested('providerDetails', 'cqcProviderId')(e.target.value)}
                sx={{ flex: 1, minWidth: 220 }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1.5 }}>
            Aims and objectives
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={sop.aimsAndObjectives || ''}
            onChange={(e) => update('aimsAndObjectives')(e.target.value)}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="title3">Regulated activities</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addRegulatedActivity}>
              Add
            </Button>
          </Stack>
          <Stack gap={2}>
            {(sop.regulatedActivities || []).map((ra, i) => (
              <Stack key={i} direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel>Activity type</InputLabel>
                  <Select
                    label="Activity type"
                    value={ra.activityType}
                    onChange={(e) => updateRegulatedActivity(i, 'activityType', e.target.value)}
                  >
                    {ACTIVITY_TYPES.map((a) => (
                      <MenuItem key={a.value} value={a.value}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Narrative"
                  value={ra.narrative || ''}
                  onChange={(e) => updateRegulatedActivity(i, 'narrative', e.target.value)}
                  sx={{ flex: 1, minWidth: 220 }}
                />
                <IconButton onClick={() => removeRegulatedActivity(i)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            {(sop.regulatedActivities || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No regulated activities added yet.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="title3">Locations</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addLocation}>
              Add
            </Button>
          </Stack>
          <Stack gap={2}>
            {(sop.locations || []).map((loc, i) => (
              <Stack key={i} direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
                <TextField
                  size="small"
                  label="Name"
                  value={loc.name || ''}
                  onChange={(e) => updateLocation(i, 'name', e.target.value)}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <TextField
                  size="small"
                  label="Address"
                  value={loc.address || ''}
                  onChange={(e) => updateLocation(i, 'address', e.target.value)}
                  sx={{ flex: 1, minWidth: 220 }}
                />
                <IconButton onClick={() => removeLocation(i)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            {(sop.locations || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No locations added yet.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1.5 }}>
            Registered manager
          </Typography>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Name"
              value={sop.registeredManager?.name || ''}
              onChange={(e) => updateNested('registeredManager', 'name')(e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Contact details"
              value={sop.registeredManager?.contactDetails || ''}
              onChange={(e) => updateNested('registeredManager', 'contactDetails')(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <TextField
              size="small"
              label="Qualifications summary"
              value={sop.registeredManager?.qualificationsSummary || ''}
              onChange={(e) => updateNested('registeredManager', 'qualificationsSummary')(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1.5 }}>
            Nominated individual
          </Typography>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Name"
              value={sop.nominatedIndividual?.name || ''}
              onChange={(e) => updateNested('nominatedIndividual', 'name')(e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Contact details"
              value={sop.nominatedIndividual?.contactDetails || ''}
              onChange={(e) => updateNested('nominatedIndividual', 'contactDetails')(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1.5 }}>
            Service user bands
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {SERVICE_USER_BAND_OPTIONS.map((band) => {
              const selected = (sop.serviceUserBands || []).includes(band)
              return (
                <Chip
                  key={band}
                  label={band}
                  color={selected ? 'primary' : 'default'}
                  onClick={() =>
                    update('serviceUserBands')(
                      selected
                        ? sop.serviceUserBands.filter((b) => b !== band)
                        : [...(sop.serviceUserBands || []), band],
                    )
                  }
                />
              )
            })}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1.5 }}>
            Safeguarding arrangements summary
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={sop.safeguardingArrangementsSummary || ''}
            onChange={(e) => update('safeguardingArrangementsSummary')(e.target.value)}
          />
        </Paper>
      </Stack>
    </PermissionCheck>
  )
}

export default SopBuilder
